// These utilities are used by generator and browser code, so using CommonJS imports

const _ = require('lodash')
const stringify = require('@aitodotai/json-stringify-pretty-compact')
const Slugger = require('./slugger')

function isRef(obj) {
  return _.has(obj, '$ref')
}

function ensureTrailingDot(text) {
  const trimmed = text.trim()
  if (!_.endsWith(trimmed, '.')) {
    return `${trimmed}.`
  }

  return trimmed
}

function ensureWithoutTrailingDot(text) {
  const trimmed = text.trim()
  if (_.endsWith(trimmed, '.')) {
    return _.trimEnd(trimmed, '.')
  }

  return trimmed
}

// Parses OpenAPI ref path to an array of attributes, which can be passed to _.get()
// Expecting the format to be #/path/in/json/tree
// Examples:
//   #/components/schemas/DecoratedHits
//   #/components/parameters/Table
//   #/components/schemas/TableSchema
function parseRef(refName) {
  if (!_.startsWith(refName, '#/')) {
    throw new Error(`OpenAPI ref not starting with '#/': ${refName}`)
  }

  // ['#', 'components', 'schemas', 'TableSchema']
  const parts = refName.split('/')

  // All but first
  return _.tail(parts)
}

function resolveOpenApiRef(bundle, obj) {
  if (isRef(obj)) {
    const refPath = parseRef(obj.$ref)
    const resolvedRef = _.get(bundle, refPath)
    if (!resolvedRef) {
      throw new Error(`Unable to resolve OpenAPI ref: ${obj.$ref}`)
    }

    return _.merge({}, resolvedRef, {
      __refPath: obj.$ref,
      __refName: _.last(obj.$ref.split('/')),
    })
  }

  return obj
}

function getOpenApiPath(bundle, partialApiObj, _attrs) {
  const attrs = _.isString(_attrs)
    ? [_attrs]
    : _.cloneDeep(_attrs)

  let currentObj = resolveOpenApiRef(bundle, partialApiObj)
  while (attrs.length > 0) {
    const currentAttr = attrs.shift()
    currentObj = resolveOpenApiRef(bundle, currentObj[currentAttr])
  }

  return currentObj
}

// Returns all schemas which are linked to operations with given tag
// The purpose is to find for example all schemas which are mentioned in the API operations
// with tag "query". These schemas are then part of our query language
//
// When opts.skipRoot = true:
//  The request and response bodies are NOT returned. So for example EvaluateQuery is not returned
//  because we have these definitions directly in the endpoint description. It would be duplication
//  to also define them in the schemas section
function getUniqueSchemasInOperations(bundle, tag, opts = {}) {
  const tags = _.isArray(tag) ? tag : [tag]
  const allSchemas = []

  _.forEach(bundle.paths, (operations) => {
    _.forEach(operations, (operation) => {
      const isRelevantOperation = _.some(tags, (tagName) => {
        return _.includes(operation.tags, tagName)
      })
      if (_.isUndefined(tag) || isRelevantOperation) {
        // We need to separately go through requestBody and response schemas to be able to
        // detect the "root" schemas, for example SearchQuery
        if (_.has(operation, 'requestBody')) {
          traverseSchemas(bundle, operation.requestBody, (schema) => {
            allSchemas.push(schema)
          }, opts)
        }

        _.forEach(operation.responses, (response) => {
          if (_.has(response, 'content')) {
            traverseSchemas(bundle, response, (schema) => {
              allSchemas.push(schema)
            }, opts)
          }
        })
      }
    })
  })

  return _.uniqBy(allSchemas, schema => schema.__refName)
}

function traverseSchemas(bundle, obj, cb, opts = {}, visited = []) {
  const arr = _.isArray(obj) ? obj : _.map(obj, val => val)

  if (isRef(obj)) {
    const resolvedRef = resolveOpenApiRef(bundle, obj)
    const isVisited = _.includes(visited, resolvedRef.__refName)
    if (!isVisited) {
      visited.push(resolvedRef.__refName)

      const isRoot = resolvedRef.__refName === visited[0]
      const shouldSkip = opts.skipRoot && isRoot
      if (!shouldSkip) {
        cb(resolvedRef)
      }

      return traverseSchemas(bundle, resolvedRef, cb, opts, visited)
    }
  }

  _.forEach(arr, (val) => {
    if (_.isObject(val) || _.isArray(val)) {
      traverseSchemas(bundle, val, cb, opts, visited)
    }
  })
}

function compileTemplate(template) {
  return _.template(template, {
    // Mustache style: {{ variable }}
    interpolate: /{{([\s\S]+?)}}/g
  })
}

// Creates a new slug without any previous state
function adhocSlugify(text) {
  const slugger = new Slugger()
  return slugger.slug(text)
}

function slugify(text, opts = {}) {
  const slugFunc = opts.slugify
    ? opts.slugify
    : t => adhocSlugify(t)

  return slugFunc(text)
}

function refNameToSlug(refName, opts = {}) {
  return slugify(`schema-${refName}`, opts)
}

function operationToSlug(operationMetadata) {
  // Should be unique
  return slugify(`${operationMetadata.method}-${operationMetadata.path}`)
}

function sectionToTitle(section) {
  if (section.schemaRef) return section.schemaRef
  else return section.title
}

function sectionToSlug(section, opts = {}) {
  if (section.schemaRef) {
    const slugger = new Slugger()
    return refNameToSlug(section.schemaRef, { 
      slugify: t => slugger.slug(t)
    })
  }
  else return slugify(`language-${sectionToTitle(section)}`, opts)
}

function refNameToPretty(content, refName) {
  const found = content.refNameMapping[refName]
  if (!found) {
    return refName
  }
  return found
}

function bestStringify(obj, opts = {}) {
  // null or undefined
  if (!obj) {
    return JSON.stringify(obj, null, 2)
  }

  // json-stringify-pretty-compact is good by default
  // but for example if the response is {"status": "ok", "entries": 3} (in one line)
  // we can just open it up to make it more readable.
  const result = stringify(obj, { objectMargins: true, maxNesting: 1 }).trim()
  if (!opts.allowSingleLine && result.split('\n').length < 2) {
    return JSON.stringify(obj, null, 2)
  }

  return result
}

function firstSentence(text) {
  let sentence = text.trim().split('\n')[0].split('.')[0].trim()
  if (!_.endsWith(sentence, '.')) {
    sentence += '.'
  }

  return sentence
}

module.exports = {
  isRef,
  parseRef,
  resolveOpenApiRef,
  getUniqueSchemasInOperations,
  getOpenApiPath,
  compileTemplate,
  operationToSlug,
  refNameToSlug,
  refNameToPretty,
  slugify,
  sectionToSlug,
  sectionToTitle,
  bestStringify,
  firstSentence,
  ensureTrailingDot,
  ensureWithoutTrailingDot,
}
